import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Chip,
    FormControlLabel,
    Switch,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import Logger from "../../../services/Logger";
import StatsSlidingService from "../../../services/StatsSlidingService";
import { CsvDownloadButton, csvNumber, downloadCsv } from "../../pzl_common/stats/adminCsvExport";
import { DEFAULT_TOP_USERS_SORT, parseTopUsersSortModel, topUsersSortModel } from "./topUsersTableSort";

const formatNumber = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—");

const formatDateTime = (epochMillis) =>
    epochMillis ? dayjs(Number(epochMillis)).format("YYYY-MM-DD HH:mm") : "—";

export default function SlidingTopUsersPanel({ language }) {
    const [excludeTest, setExcludeTest] = useState(true);
    const [excludeStaff, setExcludeStaff] = useState(true);
    const [pageModel, setPageModel] = useState({ page: 0, pageSize: 25 });
    const [sortModel, setSortModel] = useState(() =>
        topUsersSortModel(DEFAULT_TOP_USERS_SORT.field, DEFAULT_TOP_USERS_SORT.dir)
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    useEffect(() => {
        setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [language]);

    const resetPage = useCallback((update) => {
        update();
        setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, []);

    const { field: sortField, dir: sortDir } = useMemo(
        () => parseTopUsersSortModel(sortModel),
        [sortModel]
    );

    const handleSortModelChange = useCallback((model) => {
        const next = parseTopUsersSortModel(model);
        setSortModel((prev) => {
            const current = parseTopUsersSortModel(prev);
            if (current.field === next.field && current.dir === next.dir) {
                return prev;
            }
            return topUsersSortModel(next.field, next.dir);
        });
        setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, []);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await StatsSlidingService.topUsers({
                    language,
                    page: pageModel.page,
                    pageSize: pageModel.pageSize,
                    includeTest: !excludeTest,
                    excludeStaff,
                    sortField,
                    sortDir,
                });
                if (!cancelled) {
                    setResult(data ?? null);
                }
            } catch (err) {
                Logger.error("Failed to load sliding top users:", err);
                if (!cancelled) {
                    setError("Failed to load top users.");
                    setResult(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [language, excludeTest, excludeStaff, pageModel.page, pageModel.pageSize, sortField, sortDir]);

    const rows = useMemo(() => (Array.isArray(result?.users) ? result.users : []), [result]);
    const rowCount = Number(result?.total) || 0;

    const handleDownloadCsv = useCallback(() => {
        downloadCsv(
            "sliding-top-users.csv",
            [
                "rank",
                "type",
                "name",
                "email",
                "score",
                "completed",
                "updatedAt",
                "cloudUserId",
                "sessionId",
            ],
            rows.map((row) => [
                csvNumber(row?.rank),
                row?.type === "SIGNED" ? "Signed in" : "Anonymous",
                row?.name || "",
                row?.email || "",
                csvNumber(row?.score),
                csvNumber(row?.completed),
                formatDateTime(row?.updatedAt),
                row?.cloudUserId || "",
                row?.sessionId || "",
            ]),
        );
    }, [rows]);

    const columns = useMemo(
        () => [
            {
                field: "rank",
                headerName: "Rank",
                width: 80,
                sortable: false,
            },
            {
                field: "type",
                headerName: "Type",
                width: 130,
                sortable: false,
                renderCell: (params) =>
                    params.value === "SIGNED" ? (
                        <Chip size="small" color="primary" label="Signed in" />
                    ) : (
                        <Chip size="small" variant="outlined" label="Anonymous" />
                    ),
            },
            {
                field: "name",
                headerName: "Player",
                flex: 1,
                minWidth: 180,
                sortable: false,
                valueGetter: (value, row) => value || row?.email || "—",
            },
            {
                field: "email",
                headerName: "Email",
                flex: 1,
                minWidth: 200,
                sortable: false,
                valueGetter: (value) => value || "—",
            },
            {
                field: "score",
                headerName: "Score",
                width: 110,
                type: "number",
                sortable: true,
                sortingOrder: ["desc", "asc"],
                valueFormatter: (value) => formatNumber(value),
            },
            {
                field: "completed",
                headerName: "Completed",
                width: 120,
                type: "number",
                sortable: true,
                sortingOrder: ["desc", "asc"],
                valueFormatter: (value) => formatNumber(value),
            },
            {
                field: "updatedAt",
                headerName: "Updated",
                width: 170,
                sortable: true,
                sortingOrder: ["desc", "asc"],
                valueFormatter: (value) => formatDateTime(value),
            },
        ],
        []
    );

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeTest}
                            onChange={(event) => resetPage(() => setExcludeTest(event.target.checked))}
                        />
                    }
                    label="Exclude test accounts"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={excludeStaff}
                            onChange={(event) => resetPage(() => setExcludeStaff(event.target.checked))}
                        />
                    }
                    label="Exclude staff"
                />

                <Typography variant="body2" color="text.secondary">
                    {language} · signed-in players and guest sessions
                </Typography>

                <Box sx={{ ml: "auto" }}>
                    <CsvDownloadButton
                        label="Download top users CSV"
                        disabled={rows.length === 0}
                        onClick={handleDownloadCsv}
                    />
                </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click Score, Completed, or Updated to sort. Signed-in players use the leaderboard
                (1 point per completed puzzle); guests are ranked by completed puzzles.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!error && result && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <SummaryTile label="Players" value={formatNumber(result.total)} hint="Signed-in and guests" />
                    <SummaryTile label="Signed in" value={formatNumber(result.signedCount)} />
                    <SummaryTile label="Anonymous" value={formatNumber(result.guestCount)} hint="Guest sessions" />
                </Box>
            )}

            <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.id}
                rowCount={rowCount}
                loading={loading}
                paginationMode="server"
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                paginationModel={pageModel}
                onPaginationModelChange={setPageModel}
                pageSizeOptions={[15, 25, 50]}
                autoHeight
                disableRowSelectionOnClick
                hideFooterSelectedRowCount
                localeText={{ noRowsLabel: loading ? "Loading…" : "No players yet." }}
            />
        </Box>
    );
}

function SummaryTile({ label, value, hint }) {
    return (
        <Box sx={{ p: 1.25, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {value}
            </Typography>
            {hint ? (
                <Typography variant="caption" color="text.secondary">
                    {hint}
                </Typography>
            ) : null}
        </Box>
    );
}
